import React from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Container, Typography } from '@mui/material';
import { usePageTitle } from '@/shared/context/PageTitleContext';
import { usePermission } from '@/shared/hooks/usePermission';
import ReportPageSkeleton from './skeletons/ReportPageSkeleton';
import { mergeSx } from '@/shared/ui/utils/mergeSx';
import { toolbarActionsSx } from './toolbarActionsSx';

export interface ReportPageProps {
  title: string;
  /**
   * One sentence of the scope the numbers belong to, e.g. a period and a clinic.
   * Shown above the body and repeated on print.
   */
  scopeLabel?: string;
  permission?: string;
  permissionDeniedMessage?: string;
  /** Optional tabs row rendered flush under the app bar. */
  tabList?: React.ReactNode;
  /** Right side of the toolbar, before export and print. */
  filters?: React.ReactNode;
  /** Period and unit controls. Falls back to `filterBar`. */
  scope?: React.ReactNode;
  /** Export and print. Falls back to `toolbarRight`. */
  actions?: React.ReactNode;
  toolbarLeft?: React.ReactNode;
  toolbarRight?: React.ReactNode;
  filterBar?: React.ReactNode;
  /** When the figures were produced. Printed in the footer. */
  generatedAt?: Date | number | null;
  loading?: boolean;
  /** Replaces the default report skeleton when `loading` is true. */
  loadingFallback?: React.ReactNode;
  error?: string | null;
  children: React.ReactNode;
  maxWidth?: 'lg' | 'xl';
  /**
   * Page fills the area under the app bar. The body is a flex column so a
   * table can take the remaining height and scroll inside itself.
   */
  fill?: boolean;
}

/** Chrome báo cáo: phạm vi, xuất/in, dòng phạm vi đang áp, chân trang khi in. */
const ReportPage: React.FC<ReportPageProps> = ({
  title,
  scopeLabel,
  permission,
  permissionDeniedMessage = 'Bạn không có quyền truy cập trang này.',
  tabList,
  filters,
  scope,
  actions,
  toolbarLeft,
  toolbarRight,
  filterBar,
  generatedAt,
  loading = false,
  loadingFallback,
  error,
  children,
  maxWidth = 'lg',
  fill = false,
}) => {
  usePageTitle(title);
  const { t } = useTranslation('common');
  const { hasPermission } = usePermission();

  const actionControls = actions ?? toolbarRight;
  const showToolbar = true;
  const generatedLabel =
    generatedAt != null
      ? t('reportGeneratedAt', { time: format(generatedAt, 'dd/MM/yyyy HH:mm') })
      : null;

  if (permission && !hasPermission(permission)) {
    return (
      <Container maxWidth={maxWidth}>
        <Alert severity="error" sx={{ mt: 4 }}>
          {permissionDeniedMessage}
        </Alert>
      </Container>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        ...(fill
          ? {
              flex: '1 1 0',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }
          : { pb: 4 }),
        '@media print': { overflow: 'visible', height: 'auto' },
      }}
    >
      {tabList && (
        <Box
          sx={{
            bgcolor: 'common.white',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Container maxWidth={maxWidth}>{tabList}</Container>
        </Box>
      )}

      <Container
        maxWidth={maxWidth}
        sx={
          fill
            ? {
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                '@media print': { height: 'auto', overflow: 'visible' },
              }
            : undefined
        }
      >
        <Box
          sx={{
            display: 'none',
            '@media print': { display: 'block', mb: 2 },
          }}
        >
          <Typography variant="h6">{title}</Typography>
          {scopeLabel && <Typography variant="body2">{scopeLabel}</Typography>}
        </Box>

        {showToolbar && (
          <Box
            sx={mergeSx(
              {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                mt: 2,
                mb: 1,
                '@media print': { display: 'none' },
              },
              toolbarActionsSx,
              fill ? { flexShrink: 0 } : undefined,
            )}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                flexWrap: 'wrap',
                alignItems: 'center',
                flex: 1,
                minWidth: 0,
              }}
            >
              <Typography variant="h6" component="h1" noWrap>
                {title}
              </Typography>
              {toolbarLeft}
            </Box>
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap',
                alignItems: 'center',
                flexShrink: 0,
              }}
            >
              {filters}
              {scope}
              {actionControls}
            </Box>
          </Box>
        )}

        {filterBar && <Box sx={{ mb: 2 }}>{filterBar}</Box>}

        {scopeLabel && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {scopeLabel}
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (loadingFallback ?? <ReportPageSkeleton />) : fill ? (
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'auto',
              '@media print': { overflow: 'visible', height: 'auto' },
            }}
          >
            {children}
          </Box>
        ) : (
          children
        )}

        {(scopeLabel || generatedLabel) && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              display: 'none',
              '@media print': { display: 'block', mt: 3 },
            }}
          >
            {[scopeLabel, generatedLabel].filter(Boolean).join(' · ')}
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default ReportPage;
